// Annualised implied returns for a block trade, off the recommendation bundle stored on
// the snapshot.
//
// Deliberately derived on the client rather than on the backend, using the very selectors
// the Orders & Execution screen uses — one implementation, so the two screens cannot
// disagree about an analyst's target. Both the price they compound from (the snapshot's
// CMP) and the date they compound to (`captured_at`) come from the snapshot, so the figure
// stays the one the decision was taken on instead of drifting as today's date moves.

import {
  computeImpliedReturn,
  findMatchedRec,
  modelTargetAtHorizon,
  priceForIr,
  yearsToTarget,
} from '@/components/order_recommendation/recommendationSelectors';
import { BlockTradeRecommendationContext } from '@/types/blockTrade';

export interface BlockTradeImpliedReturns {
  ic: number | null;
  analyst: number | null;
  model3yr: number | null;
  model5yr: number | null;
}

const BLANK: BlockTradeImpliedReturns = {
  ic: null,
  analyst: null,
  model3yr: null,
  model5yr: null,
};

export const IMPLIED_RETURN_HINT =
  'Derived from the stored recommendations against the snapshot CMP, using the same '
  + 'selectors as the Orders & Execution screen: ((Target Price / CMP) ^ (1 / Years to Target)) - 1.';

/**
 * @param context  Recommendation bundle captured on the snapshot; null when the OMS had none.
 * @param cmp      The snapshot's CMP. Null (a live-data outage) blanks every figure rather
 *                 than compounding against a price that was never observed.
 * @param capturedAt  ISO timestamp the snapshot was taken at, used as the reference date
 *                 for "years to target". An unparseable value falls back to now.
 */
export const deriveImpliedReturns = (
  context: BlockTradeRecommendationContext | null | undefined,
  cmp: number | null | undefined,
  capturedAt: string,
): BlockTradeImpliedReturns => {
  if (!context || cmp == null) return BLANK;

  const capturedDate = new Date(capturedAt);
  const referenceDate = isNaN(capturedDate.getTime()) ? new Date() : capturedDate;
  const recs = context.recommendations;

  const forPerson = (name: string | null): number | null => {
    // Pinned to the three-year view: this page quotes one Analyst and one IC figure,
    // and the five-year horizon is reported by its own Model 5yr row below.
    const rec = findMatchedRec(recs, name, 3);
    return computeImpliedReturn(
      priceForIr(rec),
      cmp,
      yearsToTarget(rec?.target_date, referenceDate),
    );
  };

  const forModel = (years: number): number | null => {
    const { targetPrice, targetDate } = modelTargetAtHorizon(recs, years);
    return computeImpliedReturn(targetPrice, cmp, yearsToTarget(targetDate, referenceDate));
  };

  return {
    ic: forPerson(context.latest_rec_ic_user_name),
    analyst: forPerson(context.latest_rec_analyst_name),
    model3yr: forModel(3),
    model5yr: forModel(5),
  };
};
